import React, { useState, useEffect } from 'react';
import { Grid } from '@mui/material';
import CircularProgress from '@mui/material/CircularProgress';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Paper from '@mui/material/Paper';
import { useNavigate } from "react-router-dom";
import useMediaQuery from '@mui/material/useMediaQuery';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Typography from '@mui/material/Typography';
import IconButton from '@mui/material/IconButton';
import api from '../../utils/webcall';
import { getErrorMessage } from '../../utils/utils';
import { Button, Box, useTheme } from '@mui/material';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import MoreHorizIcon from '@mui/icons-material/MoreHoriz';
import UserProfile from '../../components/User';
import AccessController from '../../components/access_controller';

// add DnD import
import { DragDropContext, Droppable, Draggable } from '@hello-pangea/dnd';

function InventoryViewListRow(props) {
    const { data, index, reorder, moveUp, moveDown, isMobile, draggableProvided } = props;
    const navigate = useNavigate();

    if (isMobile) {
        return (
            // apply draggable props/ref to the Grid item so full card is draggable
            <Grid size={{ xs: 12 }} key={`inventory-card-${index}`} ref={draggableProvided ? draggableProvided.innerRef : null} {...(draggableProvided ? draggableProvided.draggableProps : {})} {...(draggableProvided ? draggableProvided.dragHandleProps : {})} sx={{ width: '100%', boxSizing: 'border-box' }}>
                <Card variant="outlined" sx={{
                    mb: 1,
                    width: '100%',
                    boxSizing: 'border-box'
                }}>
                    <CardContent sx={{ p: 1.5 }}>
                        <Grid container alignItems="center" spacing={1}>
                            {reorder && (
                                <Grid>
                                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                                        <IconButton size="small" onClick={() => moveUp(index)} disabled={index === 0}>
                                            <KeyboardArrowUpIcon fontSize="small" />
                                        </IconButton>
                                        <IconButton size="small" onClick={() => moveDown(index)}>
                                            <KeyboardArrowDownIcon fontSize="small" />
                                        </IconButton>
                                    </Box>
                                </Grid>
                            )}
                            <Grid size="grow">
                                <Typography variant="subtitle1" component="div" sx={{ fontWeight: 'medium', wordBreak: 'break-word' }}>
                                    {data['item_name']}
                                </Typography>
                                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                                    Quantity: {data['current_inventory']} {data['measurement_unit']}
                                </Typography>
                                <Typography variant="body2" color="text.secondary">
                                    Updated: {data['last_inventory_date']}
                                </Typography>
                                <Typography variant="body2" color="text.secondary">
                                    By: {data['last_update_by_user'] !== null
                                        ? `${data['last_update_by_user']['first_name']} ${data['last_update_by_user']['last_name']}`
                                        : 'N/A'}
                                </Typography>
                            </Grid>
                            <Grid>
                                <IconButton
                                    onClick={() => navigate(`/inventory/view/details/${data['item_name']}`)}
                                    size="small"
                                >
                                    <MoreHorizIcon fontSize="small" />
                                </IconButton>
                            </Grid>
                        </Grid>
                    </CardContent>
                </Card>
            </Grid>
        );
    }

    return (
        <TableRow key={`inventory-form-view-row-${index}`}>
            {reorder && (
                <TableCell>
                    <IconButton size="small" onClick={() => moveUp(index)} disabled={index === 0}>
                        <KeyboardArrowUpIcon fontSize="small" />
                    </IconButton>
                    <IconButton size="small" onClick={() => moveDown(index)}>
                        <KeyboardArrowDownIcon fontSize="small" />
                    </IconButton>
                </TableCell>
            )}
            <TableCell align="center">{data['item_name']}</TableCell>
            <TableCell align="center">{data['current_inventory']} {data['measurement_unit']}</TableCell>
            <TableCell align="center">{data['last_inventory_date']}</TableCell>
            <TableCell align="center">
                {data['last_update_by_user'] !== null
                    ? `${data['last_update_by_user']['first_name']} ${data['last_update_by_user']['last_name']}`
                    : 'N/A'}
            </TableCell>
            <TableCell align="center">
                <IconButton
                    onClick={() => navigate(`/inventory/view/details/${data['item_name']}`)}
                    size="small"
                >
                    <MoreHorizIcon fontSize="small" />
                </IconButton>
            </TableCell>
        </TableRow>
    )
}

function InventoryViewList(props) {
    const { data, reorder, moveUp, moveDown, onDragEnd } = props;
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

    if (data === null || data === undefined) {
        return <Typography>Inventory List is empty</Typography>;
    }

    if (isMobile) {
        // when reorder is active enable drag-and-drop using hello-pangea/dnd
        if (reorder) {
            return (
                <Box sx={{ width: '100%', maxWidth: '100vw' }}>
                    <DragDropContext onDragEnd={onDragEnd}>
                        <Droppable droppableId="inventory-list">
                            {(provided) => (
                                <Grid
                                    container
                                    spacing={1}
                                    sx={{ p: 1 }}
                                    ref={provided.innerRef}
                                    {...provided.droppableProps}
                                >
                                    {data.map((item, index) => (
                                        <Draggable key={`draggable-${item['item_name']}-${index}`} draggableId={`draggable-${item['item_name']}-${index}`} index={index}>
                                            {(draggableProvided) => (
                                                <InventoryViewListRow
                                                    key={`inventory-item-${index}`}
                                                    data={item}
                                                    index={index}
                                                    reorder={reorder}
                                                    moveUp={moveUp}
                                                    moveDown={moveDown}
                                                    isMobile={true}
                                                    draggableProvided={draggableProvided}
                                                />
                                            )}
                                        </Draggable>
                                    ))}
                                    {provided.placeholder}
                                </Grid>
                            )}
                        </Droppable>
                    </DragDropContext>
                </Box>
            );
        }

        // non-reorder mobile (read-only) view
        return (
            <Box sx={{ width: '100%', maxWidth: '100vw' }}>
                <Grid container spacing={1} sx={{ p: 1 }}>
                    {data.map((item, index) => (
                        <InventoryViewListRow
                            key={`inventory-item-${index}`}
                            data={item}
                            index={index}
                            reorder={reorder}
                            moveUp={moveUp}
                            moveDown={moveDown}
                            isMobile={true}
                        />
                    ))}
                </Grid>
            </Box>
        );
    }

    return (
        <Paper>
            <TableContainer
                component={Paper}
                style={{
                    overflowX: "initial"
                }}
                key="inventory-table-container"
            >
                <Table stickyHeader>
                    <TableHead>
                        <TableRow>
                            {reorder && <TableCell></TableCell>}
                            <TableCell align="center">Item Name</TableCell>
                            <TableCell align="center">Quantity</TableCell>
                            <TableCell align="center">Update time</TableCell>
                            <TableCell align="center">Updated by User</TableCell>
                            <TableCell align="center">Actions</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {data.map((item, index) => (
                            <InventoryViewListRow
                                key={`inventory-item-${index}`}
                                data={item}
                                index={index}
                                reorder={reorder}
                                moveUp={moveUp}
                                moveDown={moveDown}
                                isMobile={false}
                            />
                        ))}
                    </TableBody>
                </Table>
            </TableContainer>
        </Paper>
    );
}

function ViewInventory(props) {
    const { showErrorMessage } = props;
    const [loading, setLoading] = useState(true);
    const [inventoryData, setInventoryData] = useState(null);
    const [reorder, setReorder] = useState(false);
    const username = UserProfile.getName();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

    const loadInventoryStatus = () => {
        setLoading(true);
        api.get(`/pos/inventory/view/`).then((res) => {
            if (res.status === 200) {
                let data = res.data['inventory_items'];
                data.sort(function(a, b){
                    if(a['display_index'] < b['display_index']) return -1;
                    return 1;
                });
                setInventoryData(data);
                setLoading(false);
            }
            else {
                showErrorMessage("Unhandled. Contact Developer")
            }
        }).catch((error) => {
            showErrorMessage(getErrorMessage(error));
        });
    }

    const itemMoveUp = (index) => {
        if (index === 0) {
            return;
        }
        const temp = [...inventoryData];
        [temp[index - 1], temp[index]] = [temp[index], temp[index - 1]];
        setInventoryData(temp);
    }

    const itemMoveDown = (index) => {
        if (index === inventoryData.length - 1) {
            return;
        }
        const temp = [...inventoryData];
        [temp[index], temp[index + 1]] = [temp[index + 1], temp[index]];
        setInventoryData(temp);
    }

    // handle DnD result
    const handleDragEnd = (result) => {
        if (!result.destination) return;
        const src = result.source.index;
        const dest = result.destination.index;
        if (src === dest) return;
        const newData = Array.from(inventoryData || []);
        const [moved] = newData.splice(src, 1);
        newData.splice(dest, 0, moved);
        setInventoryData(newData);
    }

    useEffect(() => {
        loadInventoryStatus();
    }, []);

    return (
        <AccessController>
            <Paper sx={{ p: isMobile ? 1 : 2 }}>
                <Grid
                    container
                    direction="column"
                    spacing={2}
                    alignItems="center"
                >
                    <Grid>
                        <Typography variant="h4" component="h2" align="center" gutterBottom>
                            DIVASH | ADMIN
                        </Typography>
                    </Grid>
                    <Grid>
                        <Typography variant="subtitle1" gutterBottom>
                            Welcome {username}
                        </Typography>
                    </Grid>
                    <Grid container justifyContent="center" spacing={1}>
                        {/* show reorder button only after data loaded and there is data */}
                        {!reorder && !loading && inventoryData && inventoryData.length > 0 && (
                            <Grid>
                                <Button
                                    variant="contained"
                                    onClick={() => setReorder(true)}
                                    startIcon={<KeyboardArrowUpIcon />}
                                >
                                    Reorder
                                </Button>
                            </Grid>
                        )}
                        {reorder && (
                            <Grid>
                                <Button
                                    variant="contained"
                                    color="primary"
                                    onClick={() => {
                                        setReorder(false);
                                        api.post(`/pos/inventory/reorder`, inventoryData)
                                            .then((res) => {
                                                if (res.status === 200) {
                                                    // TODO: Add success message
                                                }
                                            })
                                            .catch((error) => {
                                                showErrorMessage(getErrorMessage(error));
                                            });
                                    }}
                                >
                                    Save New Order Sequence
                                </Button>
                            </Grid>
                        )}
                    </Grid>
                </Grid>

                <Box sx={{ mt: 3, display: 'flex', justifyContent: 'center' }}>
                    {loading ? (
                        <CircularProgress />
                    ) : (
                        <InventoryViewList
                            data={inventoryData}
                            reorder={reorder}
                            moveDown={itemMoveDown}
                            moveUp={itemMoveUp}
                            onDragEnd={handleDragEnd}
                        />
                    )}
                </Box>
            </Paper>
        </AccessController>
    )
}

export default ViewInventory;